/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { setTeamId } from "@microsoft/shifts-experiences-shared";
import { MobxUtils } from "sh-application";
import { AppSettingsStore } from "sh-appsettings-store";
import { IDataWithSyncState, TeamDetailsResponse } from "sh-models";
import RestClient from "sh-rest-client";
import { ECSConfigKey, ECSConfigService, GraphService } from "sh-services";
import { ITeamDatabase } from "sh-services/data/ITeamDatabase";
import CurrentTeamDataProvider from "sh-services/dataproviders/CurrentTeamDataProvider";
import { TagStore } from "sh-tag-store";
import { TeamStore, setTeamDetails } from "sh-team-store";
import { TimeOffReasonsStore } from "sh-timeoffreasons-store";

import { IMemberMapper } from "../../sh-models/member/mappers/IMemberMapper";

/**
 * Team Details Data Provider for the current team.
 * TODO: Revisit this class when teamId and groupId are consolidated to be renamed properly and using a single id.
 */
export class TeamDetailsDataProvider extends CurrentTeamDataProvider<IDataWithSyncState<TeamDetailsResponse>> {

    private teamDatabase: ITeamDatabase;
    private groupId: string;
    private tenantId: string;
    private sessionId: string;
    private memberMapper: IMemberMapper;

    /**
     * Creates a new instance of TeamDetailsDataProvider.
     * @param teamDatabase The team database.
     * @param tenantId The tenant unique identifier.
     * @param groupId The group unique identifier.
     * @param teamId The team (aka schedule) unique identifier.
     * @param sessionId The session unique identifier.
     * @param memberMapper The member mapper.
     */
    constructor(teamDatabase: ITeamDatabase, tenantId: string, groupId: string, teamId: string, sessionId: string, memberMapper: IMemberMapper) {
        super(teamId);
        this.teamDatabase = teamDatabase;
        this.groupId = groupId;
        this.tenantId = tenantId;
        this.sessionId = sessionId;
        this.memberMapper = memberMapper;
    }

    /**
     * Checks whether this data provider is associated with the current team.
     * @returns Whether this data provider is associated with the current team.
     */
    public isDataProviderForCurrentTeam(): boolean {
        return super.isDataProviderForCurrentTeam() || TeamStore().groupId === this.groupId;
    }

    /**
     * Gets data if it's found in memory otherwise returns undefined.
     * @returns Data if it's found in memory otherwise returns undefined.
     */
    async getDataFromMemoryForCurrentTeam(): Promise<IDataWithSyncState<TeamDetailsResponse> | undefined> {
        if (TeamStore().team && (TeamStore().team.id === this.teamId || TeamStore().team.groupId == this.groupId) &&
            TeamStore().members && TeamStore().roles &&
            TagStore().tags && TimeOffReasonsStore().timeOffReasons && AppSettingsStore().teamSettings) {
            // Use the data from memory only if we have some basic team info in cache
            // otherwise fall back to database/network
            return {
                data: new TeamDetailsResponse(
                    TeamStore().team,
                    MobxUtils.MapToArray(TeamStore().members),
                    TeamStore().adminRoleId, MobxUtils.MapToArray(TeamStore().roles),
                    MobxUtils.MapToArray(TagStore().tags),
                    null,
                    MobxUtils.MapToArray(TimeOffReasonsStore().timeOffReasons),
                    AppSettingsStore().teamSettings),
                isDataInSync: true,
                isPartialInSync: false
            };
        } else {
            return undefined;
        }
    }

    /**
     * Gets data if it's found in the database otherwise returns undefined.
     * @returns Data if it's found in the database otherwise returns undefined.
     */
    async getDataFromDatabase(): Promise<IDataWithSyncState<TeamDetailsResponse> | undefined> {
        const teamId = this.teamId ?? (await this.teamDatabase.getGroup(this.groupId))?.id;
        if (teamId) {
            const teamDetails = await this.teamDatabase.getTeamDetails(teamId);
            if (teamDetails) {
                return {
                    data: teamDetails,
                    isDataInSync: await this.teamDatabase.getAreTeamDetailsInSync(teamId, this.sessionId),
                    isPartialInSync: false
                };
            }
        }
        return undefined;
    }

    /**
     * Make an HTTP request to fetch the data from the network.
     * @returns The team details with its sync state.
     */
    async getDataFromNetwork(): Promise<IDataWithSyncState<TeamDetailsResponse>> {
        // TODO: Create and use TeamManager to retrieve team details and its members.
        // Data from the network is always assumed to be in sync
        const data = !!this.teamId ? await RestClient.getTeam(this.tenantId, this.teamId) : await RestClient.getGroup(this.groupId);
        let members = data.members;

        // if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableTeamMemberSync)) {
        //     const [users, ownersIds] = await Promise.all([
        //         GraphService.listGroupMembers(data.team.groupId),
        //         GraphService.listGroupOwnerIds(data.team.groupId)
        //     ]);

        //     // TODO: After cleaning up 'IMemberEntity' properties:
        //     // 1. Always use 'groupId' so we can call Graph without waiting for Shifts service response.
        //     // 2. Remove merging members with Shifts service and only map Graph data model to view model.
        //     //   a. If necessary, merge deleted members returned by Shifts service for historical data.
        //     members = this.memberMapper.mergeUsersProfilesWithMembersEntities({
        //         adminRoleId: data.adminRoleId,
        //         members: data.members,
        //         ownersIds: new Set(ownersIds),
        //         teamId: data.team.id,
        //         tenantId: data.team.tenantId,
        //         users
        //     });
        // }

        return {
            data: { ...data, members },
            isDataInSync: true,
            isPartialInSync: false
        };
    }

    /**
     * Sets team details with its sync state in memory.
     * @param data The team details with its sync state.
     */
    async setDataInMemoryForCurrentTeam(data: IDataWithSyncState<TeamDetailsResponse>): Promise<void> {
        if (data && data.data) {
            this.teamId = data.data.team.id;
            this.groupId = data.data.team.groupId;
            setTeamDetails(data.data);
            setTeamId(this.teamId);
        }
    }

    /**
     * Sets team details with its sync state in database.
     * @param data The team details with its sync state.
     */
    async setDataInDatabase(data: IDataWithSyncState<TeamDetailsResponse>): Promise<void> {
        if (data && data.data) {
            this.teamId = data.data.team.id;
            this.groupId = data.data.team.groupId;

            await this.teamDatabase.setTeamDetails(data.data);

            if (data.isDataInSync) {
                await this.teamDatabase.setAreTeamDetailsInSync(this.teamId, this.sessionId, data.isDataInSync);
            }
        }
    }

    /**
     * Do not use: this is not implemented.
     * Saves data to network.
     */
    async saveDataToNetwork(): Promise<IDataWithSyncState<TeamDetailsResponse>> {
        throw new Error("TeamDetailsDataProvider.saveDataToNetwork not implemented");
    }

    /**
     * Whether to skip refreshing data from network if it was only in the database and not in memory
     * This method gets called when we don't find the data in memory, and find something in the database.
     * Because we can't always trust that the data from the database was in-sync and not missing changes from the service,
     * this method allows the provider to inspect the state of data from the db and
     * determine if we need to make a network call to refresh this data asynchronously.
     * @param dataFromDatabase The data from database.
     * @returns Whether to skip refreshing data from network.
     */
    public async skipRefreshFromNetworkIfNotInMemory(dataFromDatabase: IDataWithSyncState<TeamDetailsResponse>): Promise<boolean> {
        if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSyncStateInIndexedDb)) {
            // force refresh from network if the sync state isn't allowed to be stored in IndexedDb
            return false;
        }
        // check if the data is in sync
        return dataFromDatabase && dataFromDatabase.isDataInSync;
    }
}